import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

/**
 * A wrapper component to provide smooth transitions between data updates
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether data is currently loading
 * @param {boolean} props.isFetching - Whether a background fetch is happening
 * @param {React.ReactNode} props.children - The content to render with transitions
 * @param {string} props.className - Additional classes for the container
 */
const SmoothTransition = ({ isLoading, isFetching, children, className = '' }) => {
  const [opacity, setOpacity] = useState(isLoading ? 0 : 1);
  const [previousChildren, setPreviousChildren] = useState(children);

  useEffect(() => {
    let timeout;

    if (isFetching) {
      // Save current content before it changes
      setPreviousChildren(children);

      // Slight fade out while fetching, but not completely
      setOpacity(0.7);
    } else {
      // When fetch completes, briefly delay before fading in new content
      timeout = setTimeout(() => {
        setOpacity(1);
      }, 50);
    }

    return () => clearTimeout(timeout);
  }, [isFetching, children]);

  // Use the current children or previous children based on loading state
  const contentToShow = isLoading ? previousChildren : children;

  return (
    <div className={`transition-opacity duration-300 ${className}`} style={{ opacity }}>
      {contentToShow}
    </div>
  );
};

SmoothTransition.propTypes = {
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

SmoothTransition.defaultProps = {
  isLoading: false,
  isFetching: false,
  className: '',
};

export default SmoothTransition;
